import { inject } from '@angular/core';
import {
    AddressDto,
    CreateFutureEventRequestDto,
    EventEntity,
    GetFreeAgentsByDateResponseDto,
    ProjectPurchaseEntity,
    ProjectSaleEntity,
    ProjectsEventsService,
    ProjectsPurchaseService,
    ProjectsSaleService,
    ProjectsService,
} from '@app/client';
import { EventMedium } from '@app/core/enums/event-medium.enum';
import { ProjectType } from '@app/core/enums/project-type.enum';
import dayjs from 'dayjs';
import { MessageService } from 'primeng/api';
import { map, Observable, tap } from 'rxjs';
import { FutureEventForm } from './future-event-form';

export class FutureEventFacade {
  projectSaleService = inject(ProjectsSaleService);
  projectPurchaseService = inject(ProjectsPurchaseService);
  projectsEventService = inject(ProjectsEventsService);
  projectService = inject(ProjectsService);
  messageService = inject(MessageService);
  lead!: ProjectSaleEntity | ProjectPurchaseEntity;

  getData(projectId: string, type: 'vendita' | 'acquisto') {
    let lead$: Observable<ProjectSaleEntity | ProjectPurchaseEntity>;

    if (type === 'vendita') {
      lead$ = this.projectSaleService.findOne(projectId);
    } else {
      lead$ = this.projectPurchaseService.findOne(projectId);
    }

    return lead$.pipe(
      map((res) => ({
        type: res.type as ProjectType,
        opportunityState: res.status?.opportunity,
        leadState: res.status?.lead,
        isRenting: res.data.isRenting || false,
        propertyProject: (<ProjectPurchaseEntity>res).data.propertyProject?.map((property) => ({
          label: (<any>property).projectSaleObj.data.propertyObj.name,
          value: property.projectSale,
        })),
      })),
    );
  }

  getAvailabilitySlots(
    projectId: string,
    start: Date,
    end: Date,
    duration: number = 1.0,
    medium: EventMedium = EventMedium.Fisico,
    assignToSelf: boolean = false,
  ): Observable<GetFreeAgentsByDateResponseDto[]> {
    if (assignToSelf) {
      return this.projectsEventService
        .getSelfAvailabilitySlots({
          start: start.toISOString(),
          end: end.toISOString(),
        })
        .pipe(map((response) => [response]));
    }

    return this.projectsEventService.getFreeAgentsByDate({
      projectId,
      start: start.toISOString(),
      end: end.toISOString(),
      duration,
      medium,
    });
  }

  createFutureEvent(request: FutureEventForm, duration: number) {
    const event: Partial<EventEntity> = {
      start: dayjs(request.slot?.slot).toDate(),
      end: dayjs(request.slot?.slot).add(duration, 'hour').toDate(),
    };

    const createFutureEvent: Partial<CreateFutureEventRequestDto> = {
      address: request.address as AddressDto,
      connectionInfo: request.connectionInfo,
      project: request.project,
      connectedProject: request.connectedProject,
      note: request.note,
      type: request.eventType as CreateFutureEventRequestDto.TypeEnum,
      medium: request.medium,
      notVisible: request.notVisible,
      event: event as EventEntity,
      agent: request.slot?.userId as string,
    };

    const save$ = request.assignToSelf
      ? this.projectService.createSelfAssignedFutureEvent(createFutureEvent as CreateFutureEventRequestDto)
      : this.projectService.createFutureEvent(createFutureEvent as CreateFutureEventRequestDto);

    return save$.pipe(
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Attività futura creata',
        });
      }),
    );
  }
}
